import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { CreateDiallerGroup, DiallerGroup } from '~pages/CampaignManagement/domain';

import { createDiallerGroup, getDiallerGroups, getDiallerGroupsByNextUrl } from './api';
import { DiallerGroupFilterType, DiallerGroupResponse } from './domain';

type Options = {
  accessFilterId?: number;
  filters?: DiallerGroupFilterType[];
  shouldFetch?: boolean;
};

const useDiallerGroupSearch = (search: string, options: Options = {}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [list, setList] = useState<DiallerGroup[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const observer = useRef<IntersectionObserver | undefined>(undefined);
  const { filters, accessFilterId, shouldFetch = true } = options;
  const stringFilters = JSON.stringify(filters);

  const getNextPage = useCallback(async () => {
    if (nextUrl !== null) {
      setLoading(true);
      setError(false);

      let resp: DiallerGroupResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getDiallerGroupsByNextUrl(nextUrl, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.groups]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    }
  }, [nextUrl]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(false);

    let resp: DiallerGroupResponse | undefined;
    try {
      axiosCancelRef.current = axios.CancelToken.source();
      resp = await getDiallerGroups(search, filters, accessFilterId, axiosCancelRef.current);
    } catch (e) {
      setError(true);
      setLoading(false);
      return;
    }

    // Returns undefined if request is canceled
    if (resp === undefined) return;

    setList(resp.groups);
    setHasMore(resp.nextPageUrl !== null);
    setNextUrl(resp.nextPageUrl);
    setLoading(false);
  }, [search, stringFilters, accessFilterId]);

  /** Ref watches for element view intersection and loads more results. Note: Should only be assigned to last element in
   * a list
   * */
  const intersectionObserverRef = useCallback(
    (node: any) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          getNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, getNextPage],
  );

  const create = useCallback(
    async (data: CreateDiallerGroup) => {
      try {
        await createDiallerGroup(data);
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      await reload();
    },
    [reload],
  );

  useEffect(() => {
    setList([]);
  }, [search, stringFilters, accessFilterId, shouldFetch]);

  useEffect(() => {
    const load = async (
      search: string,
      filters: DiallerGroupFilterType[] | undefined,
      accessFilterId: number | undefined,
    ) => {
      setLoading(true);
      setError(false);

      let resp: DiallerGroupResponse | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getDiallerGroups(search, filters, accessFilterId, axiosCancelRef.current);
      } catch (e) {
        setError(true);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setList((prev) => [...prev, ...resp!.groups]);
      setHasMore(resp.nextPageUrl !== null);
      setNextUrl(resp.nextPageUrl);
      setLoading(false);
    };

    if (shouldFetch) {
      load(search, filters, accessFilterId);
    }

    return () => {
      // Cancel request if it has already been executed
      axiosCancelRef.current.cancel();
    };
  }, [search, stringFilters, accessFilterId, shouldFetch]);

  return { loading, error, list, hasMore, create, intersectionObserverRef };
};

export default useDiallerGroupSearch;
